import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MarkovGroup } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovGroupList,
    MarkovGroupFilters,

    LoadAllMarkovGroups,
    LoadAllMarkovGroupStates,
    DeleteMarkovGroup,
    SortMarkovGroups,

    setMarkovGroupList,
    setMarkovGroupStateList,
    setMarkovGroupIds,
    setMarkovGroupSort,
    setMarkovGroupFilters,
    addModal,
} from "client/store"

import {
    Collapse,
    Container,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import MarkovGroupSearch from "client/pages/search/markovGroupSearch"
import EditGroup from "client/pages/markov/group/edit"
import Columns from "./columns"


const List = () => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)

    const groupList = useSelector((state: StoreType) => state.markovGroup.groupList)
    const groupIds = useSelector((state: StoreType) => state.markovGroup.groupIds)
    const sortColumn = useSelector((state: StoreType) => state.markovGroup.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovGroup.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovGroup.filters)
    const reloadList = useSelector((state: StoreType) => state.markovGroup.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [show, setShow] = useState(false)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAllMarkovGroups(model && model.id).then((res) => {
                dispatch(setMarkovGroupList(res))
            }),
            LoadAllMarkovGroupStates(model && model.id).then((res) => {
                dispatch(setMarkovGroupStateList(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [model, reloadList])

    useEffect(() => {
        const sortedIds = SortMarkovGroups(Object.values(groupList), sortColumn, sortOrder, filters)
        dispatch(setMarkovGroupIds(sortedIds))
    }, [groupList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MarkovGroup, order) => {
        dispatch(setMarkovGroupSort(column, order))
    }

    const onFilter = (newFilters: MarkovGroupFilters) => {
        dispatch(setMarkovGroupFilters(newFilters))
    }

    const onDelete: DeleteRow = (group: MarkovGroup, callback) => {
        DeleteMarkovGroup(group.id, model.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const groupListCopy: MarkovGroupList = groupList ? deepCopy(groupList) : {}
                delete groupListCopy[group.id]
                dispatch(setMarkovGroupList(groupListCopy))
                dispatch(setMarkovGroupIds(groupIds.filter((id) => id !== group.id)))
            })
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Collapse
            show={show}
            onCollapse={() => setShow(!show)}
            title={lang.groups}
        >
            <Container>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <MarkovGroupSearch
                        id="groupList"
                        rightOffset={editModelAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        editModelAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({
                                    key: "addGroup",
                                    content: <EditGroup groupId={0} />,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={groupList}
                    dataIds={groupIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    scrollToTopOnPagination={false}
                    onSort={onSort}
                    onDelete={onDelete}
                />
            </Container>
        </Collapse>
    )
}
export default List
